import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Plus, Trash2, X } from 'lucide-react';
import type {
  ProjectoEntryKind,
  ProjectoItemType,
  ProjectoPLItem,
  ProjectoPLItemInsert,
  ProjectoPassthroughCost,
} from '../../types/projecto';
import { PROJECTO_ITEM_TYPES, PROJECTO_ITEM_TYPE_LABELS } from '../../types/projecto';
import {
  chargeFromCommission,
  commissionPctOf,
  getItemCostRows,
  getItemEntryKind,
  getItemFigures,
} from '../../services/projectoService';
import { supabase } from '../../lib/supabase';
import { formatEuro } from '../../utils/currency';
import { Button } from '../ui/Button';
import { useModal } from '../../contexts/ModalContext';
import { reportFirstMissingField } from '../../utils/requiredFields';

interface Props {
  item?: ProjectoPLItem | null;
  defaultKind?: ProjectoEntryKind;
  fixedBudget: boolean;
  defaultCommissionPct: number;
  onSave: (data: Omit<ProjectoPLItemInsert, 'projecto_id'>) => Promise<void>;
  onClose: () => void;
}

const KINDS: { kind: ProjectoEntryKind; label: string; help: string }[] = [
  { kind: 'passthrough', label: 'Fornecedor ou talento', help: 'A agência paga e cobra ao cliente com comissão: videógrafo, bailarina, aluguer de estúdio.' },
  { kind: 'revenue', label: 'Serviço próprio', help: 'Trabalho da própria agência. Não há fornecedor: o valor é todo margem.' },
  { kind: 'cost', label: 'Custo interno', help: 'A agência paga e não cobra ao cliente. Reduz a margem.' },
];

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (text: string) => Math.max(0, parseFloat(text.replace(',', '.')) || 0);
const asText = (n: number | null | undefined) => (n ? String(round2(Number(n))) : '');
const newId = () => `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

interface CostRow {
  id: string;
  label: string;
  quantity: string;
  unit: string;
  paid: boolean;
}

const rowTotal = (r: CostRow) => round2((num(r.quantity) || 0) * num(r.unit));
const blankRow = (): CostRow => ({ id: newId(), label: '', quantity: '1', unit: '', paid: false });

function toRow(c: ProjectoPassthroughCost): CostRow {
  const quantity = Number(c.quantity) || 1;
  return {
    id: c.id,
    label: c.label ?? '',
    quantity: String(quantity),
    unit: asText(c.unit_cost ?? (Number(c.amount) || 0) / quantity),
    paid: !!c.paid,
  };
}

/** Which one was typed last decides: the other follows when the cost changes. */
type ChargeDriver = 'commission' | 'charge';

/** Everything about one line of the P&L, in the order people think about it. */
export function ProjectoPLItemModal({ item, defaultKind = 'passthrough', fixedBudget, defaultCommissionPct, onSave, onClose }: Props) {
  const { toast } = useModal();
  const nameRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<ProjectoEntryKind>(item ? getItemEntryKind(item) : defaultKind);
  const [name, setName] = useState(item?.name ?? '');
  const [itemType, setItemType] = useState<ProjectoItemType>(item?.item_type ?? 'Other');
  const [rows, setRows] = useState<CostRow[]>(() => {
    const existing = item ? getItemCostRows(item) : [];
    return existing.length > 0 ? existing.map(toRow) : [blankRow()];
  });

  // Own service: quantity × price
  const isOwnService = item ? getItemEntryKind(item) === 'revenue' : false;
  const [serviceQty, setServiceQty] = useState(isOwnService ? String(item?.quantity ?? 1) : '1');
  const [servicePrice, setServicePrice] = useState(isOwnService ? asText(item?.unit_price) : '');

  // Supplier: a new line follows its commission; an existing one keeps what was
  // agreed with the client, and its commission moves when the cost does
  const original = item ? getItemFigures(item) : null;
  const [driver, setDriver] = useState<ChargeDriver>(item ? 'charge' : 'commission');
  const [commissionText, setCommissionText] = useState(() =>
    original && original.cost > 0 ? asText(commissionPctOf(original.cost, original.charge)) : asText(defaultCommissionPct));
  const [chargeText, setChargeText] = useState(() => asText(original?.charge));

  const [details, setDetails] = useState({
    vendor_name: item?.vendor_name ?? '',
    vendor_contact: item?.vendor_contact ?? '',
    scheduled_date: item?.scheduled_date ?? '',
    contract_ref: item?.contract_ref ?? '',
    link: item?.link ?? '',
    description: item?.description ?? '',
    observations: item?.observations ?? '',
  });
  const [showDetails, setShowDetails] = useState(false);
  const detailsFilled = Object.values(details).filter(Boolean).length;
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Creators are often the ones being paid; suggest them by name
  useEffect(() => {
    supabase.from('influencers').select('full_name').order('full_name').then(({ data }) => {
      setSuppliers((data ?? []).map((d: { full_name: string }) => d.full_name).filter(Boolean));
    });
  }, []);

  const cost = round2(rows.reduce((s, r) => s + rowTotal(r), 0));
  const billsClient = !fixedBudget && kind === 'passthrough';

  const supplierCharge = driver === 'commission' ? chargeFromCommission(cost, num(commissionText)) : num(chargeText);
  const commissionAmount = round2(supplierCharge - cost);
  const shownPct = driver === 'commission' ? commissionText : asText(commissionPctOf(cost, supplierCharge));

  const serviceTotal = round2(num(serviceQty) * num(servicePrice));
  const charge = kind === 'revenue' ? serviceTotal : kind === 'cost' ? 0 : supplierCharge;
  const margin = round2(charge - cost);
  // With nothing paid yet there is no commission to speak of, only a charge
  const missingCost = billsClient && cost === 0 && charge > 0;

  const changeKind = (next: ProjectoEntryKind) => {
    setKind(next);
    // Nothing was charged on this line until now, so it starts like a new one
    if (next === 'passthrough' && driver === 'charge' && num(chargeText) === 0) {
      setDriver('commission');
      setCommissionText(asText(defaultCommissionPct));
    }
  };

  const patchRow = (id: string, patch: Partial<CostRow>) => setRows((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) => setRows((list) => (list.length === 1 ? [blankRow()] : list.filter((r) => r.id !== id)));
  const setDetail = (patch: Partial<typeof details>) => setDetails((d) => ({ ...d, ...patch }));

  const costRowsToSave = (): ProjectoPassthroughCost[] => rows
    .filter((r) => rowTotal(r) > 0 || r.label.trim())
    .map((r) => ({
      id: r.id.startsWith('legacy-') ? newId() : r.id,
      label: r.label.trim(),
      amount: rowTotal(r),
      quantity: num(r.quantity) || 1,
      unit_cost: num(r.unit),
      paid: r.paid,
    }));

  /** What a supplier line stores: untouched lines keep their "12 × 1.300€". */
  const supplierPrice = (): { quantity: number; unit_price: number } => {
    const previouslySupplier = item && getItemEntryKind(item) === 'passthrough';
    if (fixedBudget) {
      // No charge on screen: it follows the cost unless one was agreed before
      if (previouslySupplier && original && original.charge !== original.cost) {
        return { quantity: item.quantity, unit_price: item.unit_price };
      }
      return { quantity: 1, unit_price: cost };
    }
    if (previouslySupplier && original && supplierCharge === original.charge) {
      return { quantity: item.quantity, unit_price: item.unit_price };
    }
    return { quantity: 1, unit_price: supplierCharge };
  };

  const buildPayload = (): Omit<ProjectoPLItemInsert, 'projecto_id'> => {
    const price = kind === 'revenue'
      ? { quantity: num(serviceQty) || 1, unit_price: num(servicePrice) }
      : kind === 'cost'
        ? { quantity: 1, unit_price: 0 }
        : supplierPrice();
    return {
      entry_kind: kind,
      is_revenue: kind === 'revenue',
      cost_billing_mode: kind === 'passthrough' ? 'reimbursed' : 'absorbed',
      item_type: itemType,
      name: name.trim(),
      ...price,
      passthrough_breakdown: kind === 'revenue' ? [] : costRowsToSave(),
      // The commission is what's left between the two values: nothing else to keep
      margin_pct: 0,
      cost_commission_mode: 'percent',
      cost_commission_pct: 0,
      cost_commission_flat: 0,
      vendor_name: details.vendor_name.trim() || null,
      vendor_contact: details.vendor_contact.trim() || null,
      scheduled_date: details.scheduled_date || null,
      contract_ref: details.contract_ref.trim() || null,
      link: details.link.trim() || null,
      description: details.description.trim() || null,
      observations: details.observations.trim() || null,
    };
  };

  const save = async (thenAddAnother: boolean) => {
    const blocked = reportFirstMissingField([
      { key: 'pl-item-name', missing: !name.trim(), message: 'Preenche o Nome da linha para poder guardar.' },
    ], toast.error);
    if (blocked) return;
    setSaving(true);
    try {
      await onSave(buildPayload());
      if (!thenAddAnother) { onClose(); return; }
      setName('');
      setRows([blankRow()]);
      setDriver('commission');
      setCommissionText(asText(defaultCommissionPct));
      setChargeText('');
      setServicePrice('');
      setServiceQty('1');
      setDetails({ vendor_name: '', vendor_contact: '', scheduled_date: '', contract_ref: '', link: '', description: '', observations: '' });
      setSaving(false);
      nameRef.current?.focus();
    } catch {
      setSaving(false);
    }
  };

  const activeKind = KINDS.find((k) => k.kind === kind)!;
  const sectionTitle = 'text-sm font-semibold text-gray-900';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="pl-line-title">
      <div className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200">
          <h2 id="pl-line-title" className="text-base font-semibold text-gray-900 truncate">
            {item ? item.name : 'Nova linha'}
          </h2>
          <button type="button" onClick={onClose} className="p-2 -mr-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 sm:px-6 py-5 space-y-6">
          {/* With a fixed budget nothing here is charged, so the kind doesn't matter */}
          {(!fixedBudget || kind === 'revenue') && (
            <div>
              <div role="radiogroup" aria-label="Tipo de linha" className="grid grid-cols-3 gap-1 p-1 bg-gray-100 rounded-xl">
                {KINDS.map((k) => (
                  <button
                    key={k.kind}
                    type="button"
                    role="radio"
                    aria-checked={kind === k.kind}
                    onClick={() => changeKind(k.kind)}
                    className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium leading-tight transition-colors ${kind === k.kind ? 'bg-brand-ink text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              <p className="cm-help">{activeKind.help}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_11rem] gap-4">
            <div className="cm-field">
              <label htmlFor="pl-line-name" className="cm-label">Nome<span className="cm-req">*</span></label>
              <input
                id="pl-line-name"
                ref={nameRef}
                data-required-field="pl-item-name"
                className="cm-input"
                placeholder={kind === 'revenue' ? 'ex.: Gestão de redes sociais' : 'ex.: Videógrafo, dia de rodagem'}
                value={name}
                autoFocus={!item}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="cm-field">
              <label htmlFor="pl-line-type" className="cm-label">Categoria</label>
              <select id="pl-line-type" className="cm-select" value={itemType} onChange={(e) => setItemType(e.target.value as ProjectoItemType)}>
                {PROJECTO_ITEM_TYPES.map((t) => <option key={t} value={t}>{PROJECTO_ITEM_TYPE_LABELS[t]}</option>)}
              </select>
            </div>
          </div>

          {kind === 'revenue' ? (
            <div>
              <h3 className={`${sectionTitle} mb-2`}>Valor para o cliente</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="cm-field">
                  <label htmlFor="pl-service-qty" className="cm-label">Quantidade</label>
                  <input id="pl-service-qty" inputMode="decimal" className="cm-input cm-numeric" value={serviceQty} onChange={(e) => setServiceQty(e.target.value)} />
                </div>
                <div className="cm-field">
                  <label htmlFor="pl-service-price" className="cm-label">Preço</label>
                  <span className="cm-affix has-right">
                    <input id="pl-service-price" inputMode="decimal" className="cm-input cm-numeric" placeholder="0" value={servicePrice} onChange={(e) => setServicePrice(e.target.value)} />
                    <span>€</span>
                  </span>
                </div>
                <div className="cm-field">
                  <span className="cm-label">Total</span>
                  <div className="cm-readonly justify-end tabular-nums font-semibold">{formatEuro(serviceTotal)}</div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h3 className={sectionTitle}>O que a agência paga</h3>
              <p className="cm-help mb-2">Uma linha por pessoa ou fornecedor, se houver mais do que um.</p>
              <datalist id="pl-supplier-names">
                {suppliers.map((s) => <option key={s} value={s} />)}
              </datalist>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-200">
                {rows.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center gap-2 p-2" data-testid="pl-cost-row">
                    <input
                      list="pl-supplier-names"
                      aria-label="Quem recebe"
                      placeholder="Quem recebe"
                      className="cm-input !h-9 flex-1 basis-full sm:basis-0 min-w-[8rem]"
                      value={r.label}
                      onChange={(e) => patchRow(r.id, { label: e.target.value })}
                    />
                    <input
                      inputMode="decimal"
                      aria-label="Quantidade"
                      title="Quantidade"
                      className="cm-input cm-numeric !h-9 !w-14"
                      value={r.quantity}
                      onChange={(e) => patchRow(r.id, { quantity: e.target.value })}
                    />
                    <span className="text-xs text-gray-400">×</span>
                    <span className="cm-affix has-right w-28">
                      <input
                        inputMode="decimal"
                        aria-label="Valor"
                        placeholder="Valor"
                        className="cm-input cm-numeric !h-9"
                        value={r.unit}
                        onChange={(e) => patchRow(r.id, { unit: e.target.value })}
                      />
                      <span>€</span>
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={r.paid}
                      onClick={() => patchRow(r.id, { paid: !r.paid })}
                      className={`ml-auto h-7 px-2.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                        r.paid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {r.paid ? 'Pago' : 'Por pagar'}
                    </button>
                    <button type="button" onClick={() => removeRow(r.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Remover custo" title="Remover">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setRows((list) => [...list, blankRow()])} className="cm-btn-add !py-1.5 mt-2">
                <Plus className="w-4 h-4" /> Outro custo
              </button>
            </div>
          )}

          {billsClient && (
            <div>
              <h3 className={`${sectionTitle} mb-2`}>O que cobramos ao cliente</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="cm-field">
                  <label htmlFor="pl-commission" className="cm-label">Comissão</label>
                  <span className="cm-affix has-right">
                    <input
                      id="pl-commission"
                      inputMode="decimal"
                      className="cm-input cm-numeric"
                      placeholder="0"
                      value={missingCost ? '' : shownPct}
                      onChange={(e) => { setDriver('commission'); setCommissionText(e.target.value); }}
                    />
                    <span>%</span>
                  </span>
                </div>
                <div className="cm-field">
                  <span className="cm-label">Em euros</span>
                  <div className={`cm-readonly justify-end tabular-nums font-medium ${commissionAmount < 0 ? 'text-red-700' : ''}`}>{missingCost ? '—' : formatEuro(commissionAmount)}</div>
                </div>
                <div className="cm-field col-span-2 sm:col-span-1">
                  <label htmlFor="pl-charge" className="cm-label">Cobrado ao cliente</label>
                  <span className="cm-affix has-right">
                    <input
                      id="pl-charge"
                      inputMode="decimal"
                      className="cm-input cm-numeric font-semibold"
                      placeholder="0"
                      value={driver === 'charge' ? chargeText : asText(supplierCharge)}
                      onChange={(e) => { setDriver('charge'); setChargeText(e.target.value); }}
                    />
                    <span>€</span>
                  </span>
                </div>
              </div>
              <p className={missingCost ? 'mt-1 text-xs text-amber-800' : 'cm-help'}>
                {missingCost ? 'Indica em cima o que a agência paga para saber a comissão.' : 'Muda a comissão ou o valor cobrado: o outro acompanha.'}
              </p>
            </div>
          )}

          <div>
            <button type="button" onClick={() => setShowDetails((v) => !v)} aria-expanded={showDetails} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900">
              <ChevronRight className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
              Mais detalhes
              <span className="text-xs font-normal text-gray-500">
                {detailsFilled > 0 ? `${detailsFilled} ${detailsFilled === 1 ? 'preenchido' : 'preenchidos'}` : 'contacto, data, referência, notas'}
              </span>
            </button>
            {showDetails && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <div className="cm-field">
                  <label htmlFor="pl-vendor" className="cm-label">Entidade</label>
                  <input id="pl-vendor" className="cm-input" placeholder="Empresa que fatura" value={details.vendor_name} onChange={(e) => setDetail({ vendor_name: e.target.value })} />
                </div>
                <div className="cm-field">
                  <label htmlFor="pl-contact" className="cm-label">Contacto</label>
                  <input id="pl-contact" className="cm-input" placeholder="Email ou telefone" value={details.vendor_contact} onChange={(e) => setDetail({ vendor_contact: e.target.value })} />
                </div>
                <div className="cm-field">
                  <label htmlFor="pl-date" className="cm-label">Data</label>
                  <input id="pl-date" type="date" className="cm-date" value={details.scheduled_date} onChange={(e) => setDetail({ scheduled_date: e.target.value })} />
                </div>
                <div className="cm-field">
                  <label htmlFor="pl-ref" className="cm-label">Contrato ou referência</label>
                  <input id="pl-ref" className="cm-input" value={details.contract_ref} onChange={(e) => setDetail({ contract_ref: e.target.value })} />
                </div>
                <div className="cm-field sm:col-span-2">
                  <label htmlFor="pl-link" className="cm-label">Link</label>
                  <input id="pl-link" type="url" className="cm-input" placeholder="https://..." value={details.link} onChange={(e) => setDetail({ link: e.target.value })} />
                </div>
                <div className="cm-field sm:col-span-2">
                  <label htmlFor="pl-description" className="cm-label">Descrição</label>
                  <textarea id="pl-description" className="cm-textarea !min-h-[3.5rem]" rows={2} value={details.description} onChange={(e) => setDetail({ description: e.target.value })} />
                </div>
                <div className="cm-field sm:col-span-2">
                  <label htmlFor="pl-notes" className="cm-label">Notas</label>
                  <textarea id="pl-notes" className="cm-textarea !min-h-[3.5rem]" rows={2} value={details.observations} onChange={(e) => setDetail({ observations: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 px-5 sm:px-6 py-3 space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm" data-testid="pl-line-result">
            {kind !== 'revenue' && <span className="text-gray-600">Custo <strong className="text-gray-900 tabular-nums">{formatEuro(cost)}</strong></span>}
            {(billsClient || kind === 'revenue') && <span className="text-gray-600">Cobrado <strong className="text-gray-900 tabular-nums">{formatEuro(charge)}</strong></span>}
            {missingCost && <span className="text-xs font-medium text-amber-800">Falta o custo: margem por apurar</span>}
            {(billsClient || kind === 'revenue') && !missingCost && (
              <span className="text-gray-600">Margem <strong className={`tabular-nums ${margin < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{formatEuro(margin)}</strong></span>
            )}
            {kind === 'cost' && !fixedBudget && <span className="text-xs text-gray-500">Não é cobrado ao cliente</span>}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancelar</Button>
            {!item && <Button variant="secondary" size="sm" disabled={saving} onClick={() => save(true)}>Guardar e adicionar outra</Button>}
            <Button variant="primary" size="sm" isLoading={saving} onClick={() => save(false)}>Guardar</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
